// src/SetupWizard/Step5SEO.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  LocationOn as LocationIcon,
  CompareArrows as CompetitorIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalOffer as NicheIcon,
} from '@mui/icons-material';

function Step5SEO({ seoFocus, onChange }) {
  const [selectedFocus, setSelectedFocus] = useState(seoFocus || []);
  const [error, setError] = useState('');

  useEffect(() => {
    // Validate initial state
    validateAndUpdate(selectedFocus);
  }, []);

  const validateAndUpdate = (focuses) => {
    if (!focuses.length) {
      setError('Please select at least one SEO strategy');
      onChange({ seoFocus: focuses, isValid: false });
    } else {
      setError('');
      onChange({ seoFocus: focuses, isValid: true });
    }
  };

  const seoOptions = [
    {
      value: "Broad Keywords",
      icon: <SearchIcon />,
      description: "High-traffic general terms",
      tooltip: "Target popular, high-volume keywords (e.g., 'healthy recipes', 'travel tips')"
    },
    {
      value: "Long-Tail",
      icon: <TrendingIcon />,
      description: "Specific search phrases",
      tooltip: "Focus on detailed queries (e.g., 'gluten-free breakfast recipes under 300 calories')"
    },
    {
      value: "Local SEO",
      icon: <LocationIcon />,
      description: "Location-based content",
      tooltip: "Optimize for local searches (e.g., 'best coffee shops in [city]')"
    },
    {
      value: "Competitor Focus",
      icon: <CompetitorIcon />,
      description: "Target competitor keywords",
      tooltip: "Target keywords your competitors rank for to capture market share"
    },
    {
      value: "High Intent",
      icon: <ShoppingCartIcon />,
      description: "Purchase-ready audience",
      tooltip: "Focus on keywords that drive sales (e.g., 'buy', 'best', 'review')"
    },
    {
      value: "Niche Topics",
      icon: <NicheIcon />,
      description: "Specialized content",
      tooltip: "Target specific industry or hobby-related keywords"
    }
  ];

  const handleSeoFocusToggle = (value) => {
    let updatedFocus;
    if (selectedFocus.includes(value)) {
      updatedFocus = selectedFocus.filter(focus => focus !== value);
    } else {
      if (selectedFocus.length >= 3) return;
      updatedFocus = [...selectedFocus, value];
    }
    setSelectedFocus(updatedFocus);
    validateAndUpdate(updatedFocus);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Step 5: Set Your SEO Strategy
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose up to 3 SEO focus areas to optimize your content for search engines
        </Typography>
      </Box>

      {/* Selected Strategies Display */}
      {selectedFocus.length > 0 && (
        <Paper 
          sx={{ 
            p: 1.5, 
            mb: 2, 
            bgcolor: '#f0f7ff',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography variant="body2" sx={{ mr: 1 }}>
            Selected ({selectedFocus.length}/3):
          </Typography>
          {selectedFocus.map((focus) => (
            <Chip
              key={focus}
              label={focus}
              onDelete={() => handleSeoFocusToggle(focus)}
              color="primary"
              size="small"
            />
          ))}
        </Paper>
      )}

      {/* SEO Options Grid */}
      <Grid container spacing={2}>
        {seoOptions.map(({ value, icon, description, tooltip }) => (
          <Grid item xs={12} sm={6} md={4} key={value}>
            <Tooltip title={tooltip} placement="top">
              <Card
                onClick={() => handleSeoFocusToggle(value)}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  transform: selectedFocus.includes(value) ? 'scale(1.02)' : 'scale(1)',
                  border: theme => selectedFocus.includes(value)
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid transparent',
                  opacity: selectedFocus.length >= 3 && !selectedFocus.includes(value) ? 0.6 : 1,
                  '&:hover': {
                    transform: selectedFocus.length < 3 || selectedFocus.includes(value)
                      ? 'scale(1.02)'
                      : 'scale(1)',
                    boxShadow: 2
                  }
                }}
              >
                <CardContent sx={{ p: '12px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      p: 0.5,
                      borderRadius: 1,
                      bgcolor: theme => selectedFocus.includes(value) 
                        ? 'primary.main' 
                        : 'action.selected',
                      color: theme => selectedFocus.includes(value) 
                        ? 'white' 
                        : 'inherit'
                    }}>
                      {icon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                        {value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {/* Tips Section - Now more compact */}
      <Paper sx={{ p: 1.5, mt: 2, bgcolor: '#f5f5f5' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            💡 Tips:
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Combine broad keywords with specific ones • Use local SEO if location matters • 
            Monitor competitors • Focus on conversion keywords for monetization
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

export default Step5SEO;
